"use client";

import { useState, useRef, useEffect, type ReactNode } from "react";
import { useUser } from "@clerk/nextjs";
import { Comment, CommentFormData } from "@/lib/types";
import CommentForm from "@/components/CommentForm";
import MembershipBadge from "@/components/MembershipBadge";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { renderMixedContent } from "@/lib/renderContentUtils";
import { getCommentAnchorId } from "@/lib/comment-links";

interface CommentItemProps {
  comment: Comment;
  onReply: (parentId: string, data: CommentFormData) => boolean | void | Promise<boolean | void>;
  onEdit: (commentId: string, email: string, data: CommentFormData) => boolean | void | Promise<boolean | void>;
  onDelete: (commentId: string, email: string) => void;
  onLike: (commentId: string) => void;
  likedCommentIds: Set<string>;
  depth?: number; // 답글 깊이 (인덴트용)
  shouldTruncate?: boolean; // 200자 이상일 때 truncate 적용 여부
  /**
   * 액션 버튼 영역에 덧붙일 추가 요소 렌더러 (복원 게시물 댓글 thumbs-down 등).
   * 답글에도 재귀적으로 전달됨.
   */
  extraActions?: (comment: Comment) => ReactNode;
  /**
   * 딥링크(`#comment-{id}`) 대상 댓글 id (JCG-3603). 대상이면 접힌 본문(line-clamp)을 펼쳐서 보여준다.
   * 답글에도 재귀적으로 전달됨.
   */
  deepLinkTargetId?: string | null;
  /** 딥링크 스크롤 직후 강조 중인 댓글 id (JCG-3603). 대상이면 amber 링·배경을 잠시 입힌다. */
  highlightedCommentId?: string | null;
  /** 관리자 모더레이션: 작성자가 아니어도 인증 모달 없이 바로 삭제할 수 있다(수정은 불가). 답글에도 전달. */
  canModerate?: boolean;
  /** true 면 답글 버튼을 숨긴다(로그인 전용 게시판의 비로그인 사용자, JCG-3029). 재귀 답글에도 전달. */
  replyDisabled?: boolean;
}

/**
 * 개별 댓글 컴포넌트
 * 댓글 표시, 답글/수정/삭제 기능, 중첩 답글 지원
 */
export default function CommentItem({
  comment,
  onReply,
  onEdit,
  onDelete,
  onLike,
  likedCommentIds,
  depth = 0,
  shouldTruncate = false,
  extraActions,
  deepLinkTargetId = null,
  highlightedCommentId = null,
  canModerate = false,
  replyDisabled = false,
}: CommentItemProps) {
  const [isLiking, setIsLiking] = useState(false);
  const isLikedByCurrentUser = likedCommentIds.has(comment.id);
  const { isSignedIn, user } = useUser();
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState<"edit" | "delete" | null>(
    null
  );
  const [authEmail, setAuthEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [imageError, setImageError] = useState(false);
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  // JCG-3603: 딥링크 대상은 본문을 펼친 채로 보여준다. 단, 접힌 상태로 마운트해 overflow 를 먼저 잰 뒤
  // 펼쳐야 "접기" 버튼(isOverflowing && isExpanded)이 보장된다. 같은 대상 id 에 대해 한 번만 펼치므로
  // (revealedDeepLinkTargetId) 사용자가 접은 뒤 다시 펼치지 않고, 해시가 다른 댓글로 옮겨갔다 돌아오면 다시 펼친다.
  const isDeepLinkTarget = deepLinkTargetId !== null && deepLinkTargetId === comment.id;
  const isHighlighted = highlightedCommentId !== null && highlightedCommentId === comment.id;
  const [isExpanded, setIsExpanded] = useState(false);
  const [revealedDeepLinkTargetId, setRevealedDeepLinkTargetId] = useState<string | null>(null);
  const shouldRevealDeepLink = isDeepLinkTarget && revealedDeepLinkTargetId !== deepLinkTargetId;
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // CSS line-clamp 적용 후 overflow 여부만 한 번 체크 (rAF로 forced reflow 방지)
  useEffect(() => {
    const content = comment.content || "";
    if (!contentRef.current || !content) {
      setIsOverflowing(false);
      return;
    }

    // 펼친 상태에서는 overflow 체크 불필요 (이전 값 유지)
    if (isExpanded) return;

    const el = contentRef.current;
    const frameId = requestAnimationFrame(() => {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
      // 딥링크 대상은 측정을 마친 뒤 펼친다 (JCG-3603)
      if (shouldRevealDeepLink) {
        setRevealedDeepLinkTargetId(deepLinkTargetId);
        setIsExpanded(true);
      }
    });

    return () => cancelAnimationFrame(frameId);
  }, [comment.content, shouldTruncate, isExpanded, shouldRevealDeepLink, deepLinkTargetId]);

  // 현재 로그인한 사용자가 이 댓글의 작성자인지 확인
  const isCommentOwner = isSignedIn && user && comment.clerkUserId === user.id;

  // 좋아요 핸들러
  const handleLike = async () => {
    if (isLiking) return;
    setIsLiking(true);
    try {
      await onLike(comment.id);
    } finally {
      setIsLiking(false);
    }
  };

  // 답글 제출 핸들러
  const handleReplySubmit = async (data: CommentFormData) => {
    const result = await onReply(comment.id, data);
    if (result !== false) {
      setShowReplyForm(false);
    }
    return result;
  };

  // 수정 제출 핸들러
  const handleEditSubmit = async (data: CommentFormData) => {
    const result = await onEdit(comment.id, comment.email, data);
    if (result !== false) {
      setShowEditForm(false);
    }
    return result;
  };

  // 이메일 인증 핸들러 (비로그인 사용자용)
  const handleAuth = async () => {
    if (!authEmail.trim()) {
      alert("이메일을 입력해주세요.");
      return;
    }

    if (authEmail !== comment.email) {
      alert("이메일이 일치하지 않습니다.");
      return;
    }

    // 이메일이 맞으면 인증 코드 발송
    try {
      const response = await fetch("/api/auth/send-verification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: authEmail,
          commentId: comment.id,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        alert("인증 코드가 이메일로 발송되었습니다.");
        setShowVerificationInput(true);
      } else {
        alert(result.error || "인증 코드 발송에 실패했습니다.");
      }
    } catch (error) {
      console.error("인증 코드 발송 오류:", error);
      alert("인증 코드 발송에 실패했습니다.");
    }
  };

  // 인증 코드 확인 핸들러
  const handleVerification = async (action: "edit" | "delete") => {
    if (!verificationCode.trim()) {
      alert("인증 코드를 입력해주세요.");
      return;
    }

    try {
      const response = await fetch("/api/auth/verify-code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: authEmail,
          commentId: comment.id,
          code: verificationCode,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setShowAuthModal(null);
        setAuthEmail("");
        setVerificationCode("");
        setShowVerificationInput(false);

        if (action === "edit") {
          setShowEditForm(true);
        } else if (action === "delete") {
          if (confirm("정말로 이 댓글을 삭제하시겠습니까?")) {
            onDelete(comment.id, authEmail);
          }
        }
      } else {
        alert(result.error || "인증 코드가 올바르지 않습니다.");
      }
    } catch (error) {
      console.error("인증 코드 검증 오류:", error);
      alert("인증 코드 검증에 실패했습니다.");
    }
  };

  // 로그인된 사용자의 직접 액션 (이메일 확인 없음)
  const handleDirectAction = (action: "edit" | "delete") => {
    if (action === "edit") {
      setShowEditForm(true);
    } else if (action === "delete") {
      if (confirm("정말로 이 댓글을 삭제하시겠습니까?")) {
        onDelete(comment.id, comment.email); // 기존 이메일 전달
      }
    }
  };

  // 날짜 포매팅 함수
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      // hour12: false, // 24시간 형식 사용
    });
  };

  // 답글 인덴트 스타일 (Tailwind 동적 클래스 문제 해결)
  const getIndentStyle = (depth: number) => {
    if (depth === 0) return {};
    const indentValue = Math.min(depth * 32, 128); // 32px씩 증가, 최대 128px
    return { marginLeft: `${indentValue}px` };
  };

  return (
    <div style={getIndentStyle(depth)}>
      {/* 댓글 본체 — 딥링크 앵커 id=comment-{id} (JCG-3603, comment-links.ts 규약). 답글 목록은 이 밖의
          형제로 두어 답글이 많은 루트도 자기 본문만 중앙 정렬·강조된다. scroll-mt-24 는 PostLayout 의
          #comments 래퍼와 같은 고정 헤더 오프셋. -mx-2/px-2 는 레이아웃을 바꾸지 않고 강조 배경에
          좌우 여백만 준다. 임시 댓글(temp_…)에도 앵커가 붙지만 해시 규약(UUID)상 대상이 될 수 없어 무해. */}
      <div
        id={getCommentAnchorId(comment.id)}
        className={cn(
          "py-4 -mx-2 px-2 rounded-md scroll-mt-24 transition-[background-color,box-shadow] duration-700 ease-out motion-reduce:transition-none",
          isHighlighted &&
            "bg-amber-50 ring-2 ring-amber-300 dark:bg-amber-950/40 dark:ring-amber-800"
        )}
      >
        {/* 댓글 헤더 */}
        <div className="flex items-start gap-3 mb-2">
          {/* 프로필 이미지 */}
          <div className="flex-shrink-0">
            {comment.isDeleted ? (
              <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ?
                </span>
              </div>
            ) : comment.profileImageUrl && !imageError ? (
              <Image
                src={comment.profileImageUrl}
                alt="프로필"
                width={32}
                height={32}
                className="w-8 h-8 rounded-full"
                onError={() => setImageError(true)}
              />
            ) : (
              <Image
                src="/default_gamja.webp"
                alt="기본 프로필"
                width={32}
                height={32}
                className="w-8 h-8 rounded-full"
              />
            )}
          </div>

          {/* 사용자 정보 */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[#333] dark:text-neutral-200 text-sm">
                {comment.isDeleted ? "삭제된 사용자" : comment.author}
              </span>
              {comment.clerkUserId && (
                <MembershipBadge passType={comment.passType} />
              )}
            </div>
            <div className="text-xs text-[#706E6E] dark:text-gray-400 font-light tabular-nums">
              <span>최초 작성: {formatDate(comment.createdAt)}</span>
              {comment.lastModifiedAt && !comment.isDeleted && (
                <span className="ml-2">
                  최종 수정: {formatDate(comment.lastModifiedAt)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 댓글 내용 또는 수정 폼 */}
        {showEditForm ? (
          <div className="mb-4">
            <CommentForm
              onSubmit={handleEditSubmit}
              onCancel={() => setShowEditForm(false)}
              initialData={{
                author: comment.author || "",
                email: comment.email,
                content: comment.content || "",
              }}
              isEdit={true}
            />
          </div>
        ) : (
          <div className="mb-3">
            {comment.isDeleted ? (
              <p className="text-[#706E6E] dark:text-gray-400 italic font-light text-sm">
                삭제된 댓글입니다.
              </p>
            ) : (
              <>
                <div
                  ref={contentRef}
                  className={cn(
                    "text-[#333] dark:text-neutral-100 font-light text-sm",
                    !isExpanded && (shouldTruncate ? "line-clamp-2" : "line-clamp-5")
                  )}
                >
                  <div className="comment-content">
                    {renderMixedContent(comment.content || "")}
                  </div>
                </div>
                {isOverflowing && !isExpanded && (
                  <button
                    onClick={() => setIsExpanded(true)}
                    className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer mt-1"
                  >
                    ... 더보기
                  </button>
                )}
                {isOverflowing && isExpanded && (
                  <button
                    onClick={() => setIsExpanded(false)}
                    className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline mt-1"
                  >
                    접기
                  </button>
                )}
                {/* 첨부 이미지 표시 */}
                {comment.imageUrls && comment.imageUrls.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {comment.imageUrls.map((url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Image
                          src={url}
                          alt={`첨부 이미지 ${index + 1}`}
                          width={200}
                          height={200}
                          className="max-w-[200px] max-h-[200px] object-cover rounded-md border border-gray-200 dark:border-neutral-500 hover:opacity-80 transition-opacity"
                        />
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* 액션 버튼들 */}
        {!showEditForm && (
          <div className="flex items-center gap-2 text-xs">
            {/* 좋아요 버튼 */}
            <button
              onClick={handleLike}
              disabled={isLiking}
              className={cn(
                "flex items-center gap-1 transition-colors font-light",
                isLikedByCurrentUser
                  ? "text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                  : "text-[#706E6E] hover:text-red-500 dark:text-neutral-400 dark:hover:text-red-400",
                isLiking && "opacity-50 cursor-not-allowed"
              )}
              title={isLikedByCurrentUser ? "좋아요 취소" : "좋아요"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill={isLikedByCurrentUser ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth={2}
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                />
              </svg>
              {(comment.likeCount ?? 0) > 0 && (
                <span>{comment.likeCount}</span>
              )}
            </button>
            {!replyDisabled && (
              <button
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="text-[#706E6E] hover:text-[#5A5858] dark:text-neutral-400 dark:hover:text-neutral-300 font-light"
              >
                답글
              </button>
            )}
            {!comment.isDeleted && (
              <>
                <button
                  onClick={() => {
                    if (isCommentOwner) {
                      handleDirectAction("edit");
                    } else {
                      setShowAuthModal("edit");
                    }
                  }}
                  className="text-[#706E6E] hover:text-[#5A5858] dark:text-neutral-400 dark:hover:text-neutral-300 font-light"
                >
                  수정
                </button>
                <button
                  onClick={() => {
                    if (isCommentOwner || canModerate) {
                      handleDirectAction("delete");
                    } else {
                      setShowAuthModal("delete");
                    }
                  }}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-light"
                >
                  삭제
                </button>
              </>
            )}
            {extraActions && (
              <div className="ml-auto">{extraActions(comment)}</div>
            )}
          </div>
        )}

        {/* 답글 작성 폼 */}
        {showReplyForm && (
          <div className="mt-4 pl-4 border-l-2 border-[#706E6E] dark:border-neutral-400">
            <CommentForm
              onSubmit={handleReplySubmit}
              onCancel={() => setShowReplyForm(false)}
              isReply={true}
            />
          </div>
        )}

      </div>

      {/* 답글 목록 — 본체(앵커) 밖 형제 (JCG-3603). pb-4 는 본체 안에 있을 때 받던 py-4 아래 여백을
          이어받아 부모 액션↔첫 답글, 마지막 답글↔다음 댓글 간격을 그대로 유지한다. */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="pb-4">
          {comment.replies.map((reply, index) => {
            const total = comment.replies!.length;
            // 부모 댓글이 truncate 상태면 모든 답글 truncate, 아니면 기존 규칙 적용
            const isFirst2 = index < 2;
            const isLast2 = index >= total - 2;
            const replyShouldTruncate = shouldTruncate || (!isFirst2 && !isLast2);

            return (
              <CommentItem
                key={reply.id}
                comment={reply}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                onLike={onLike}
                likedCommentIds={likedCommentIds}
                depth={depth + 1}
                shouldTruncate={replyShouldTruncate}
                extraActions={extraActions}
                deepLinkTargetId={deepLinkTargetId}
                highlightedCommentId={highlightedCommentId}
                canModerate={canModerate}
                replyDisabled={replyDisabled}
              />
            );
          })}
        </div>
      )}

      {/* 이메일 인증 모달 */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-md shadow-lg max-w-md w-full mx-4 border-[0.5px] border-gray-200 dark:border-neutral-400">
            <h3 className="text-base sm:text-lg font-semibold mb-4 text-[#333] dark:text-neutral-200">
              {showAuthModal === "edit" ? "댓글 수정" : "댓글 삭제"}
            </h3>

            {!showVerificationInput ? (
              <>
                <p className="text-[#706E6E] dark:text-neutral-300 mb-4 text-sm font-light">
                  본인 확인을 위해 댓글 작성 시 사용한 이메일을 입력해주세요.
                </p>
                <input
                  type="email"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  placeholder="이메일"
                  className="w-full px-3 py-2 border-[0.5px] border-gray-200 dark:border-neutral-400 rounded-md focus:outline-none focus:ring-2 focus:ring-[#706E6E] dark:bg-gray-700 dark:text-white mb-4 text-sm"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAuth();
                    }
                  }}
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => {
                      setShowAuthModal(null);
                      setAuthEmail("");
                      setShowVerificationInput(false);
                    }}
                    className="px-4 py-2 text-[#706E6E] border-[0.5px] border-gray-200 dark:border-neutral-400 rounded-md hover:bg-gray-50 dark:text-neutral-300 dark:hover:bg-gray-700 text-sm font-light"
                  >
                    취소
                  </button>
                  <button
                    onClick={() => handleAuth()}
                    className="px-4 py-2 bg-[#706E6E] text-white rounded-md hover:bg-[#5A5858] text-sm font-medium"
                  >
                    인증 코드 발송
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-[#706E6E] dark:text-neutral-300 mb-4 text-sm font-light">
                  이메일로 발송된 6자리 인증 코드를 입력해주세요.
                </p>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="인증 코드 6자리"
                  className="w-full px-3 py-2 border-[0.5px] border-gray-200 dark:border-neutral-400 rounded-md focus:outline-none focus:ring-2 focus:ring-[#706E6E] dark:bg-gray-700 dark:text-white mb-4 text-sm text-center"
                  maxLength={6}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleVerification(showAuthModal);
                    }
                  }}
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => {
                      setShowVerificationInput(false);
                      setVerificationCode("");
                    }}
                    className="px-4 py-2 text-[#706E6E] border-[0.5px] border-gray-200 dark:border-neutral-400 rounded-md hover:bg-gray-50 dark:text-neutral-300 dark:hover:bg-gray-700 text-sm font-light"
                  >
                    뒤로
                  </button>
                  <button
                    onClick={() => {
                      setShowAuthModal(null);
                      setAuthEmail("");
                      setVerificationCode("");
                      setShowVerificationInput(false);
                    }}
                    className="px-4 py-2 text-[#706E6E] border-[0.5px] border-gray-200 dark:border-neutral-400 rounded-md hover:bg-gray-50 dark:text-neutral-300 dark:hover:bg-gray-700 text-sm font-light"
                  >
                    취소
                  </button>
                  <button
                    onClick={() => handleVerification(showAuthModal)}
                    className="px-4 py-2 bg-[#706E6E] text-white rounded-md hover:bg-[#5A5858] text-sm font-medium"
                  >
                    확인
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
